import { configs } from '../configs';

/**
 * Service for verifying user passwords for re-authentication
 */
class PasswordVerificationService {
  /**
   * Verify user password without logging in
   * This calls the login endpoint but handles the response to verify password only
   */
  async verifyPassword(password: string): Promise<boolean> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('User not found');
      }

      const usernameOrEmail =
        (user as any).username || (user as any).email || '';

      if (!usernameOrEmail) {
        throw new Error('Username or email not found');
      }

      // Call the login endpoint to verify password
      // We'll catch errors to determine if password is valid
      const response = await fetch(`${configs.apiRoot}/authentication/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(usernameOrEmail.includes('@')
            ? { email: usernameOrEmail }
            : { username: usernameOrEmail }),
          password,
        }),
      });

      // If response is successful, password is correct
      if (response.ok) {
        return true;
      }

      // If unauthorized, password is incorrect
      if (response.status === 401) {
        return false;
      }

      // For other errors, assume password might be incorrect
      return false;
    } catch (error) {
      console.error('Password verification error:', error);
      return false;
    }
  }

  /**
   * Get current user from localStorage or sessionStorage
   */
  private getCurrentUser(): any {
    try {
      // Try to get from user store data in localStorage
      const userStorage = localStorage.getItem('user-storage');
      if (userStorage) {
        const parsed = JSON.parse(userStorage);
        return parsed.state?.user || null;
      }

      // Fallback to auth data
      const email = localStorage.getItem('auth.email');
      const username = localStorage.getItem('auth.username');
      if (email || username) {
        return { email, username };
      }

      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }
}

export default new PasswordVerificationService();
